/**
 * Utilities Module for Murphy Door Configurator
 *
 * Provides shared utility functions for:
 * - Currency formatting
 * - Selection state checking
 * - Image lazy loading
 * - URL management
 *
 * Dependencies:
 * - md-config-selection-manager.js
 *
 * This module is used by all other modules for utility functions
 */
/**
 * Module uses scoped pattern for Shopify compatibility
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  // Pattern #19: module-level CONSTANTS reference (used inside handleSystemError)
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // ── replaceStateQuietly: prototype-integrity state ──────────────────────
  //
  // Captured when Phase 0 loads -- before every other configurator module and
  // before most third-party embeds have run. The question that actually matters
  // is "has History.prototype.replaceState been REPLACED since the page
  // started", so identity against this snapshot answers it directly.
  //
  // 🪤 A `[native code]` toString test was tried first and rejected: jsdom
  // implements replaceState in JavaScript, so it reports "patched" in every
  // test environment, and so would any browser carrying a History polyfill.
  // That check answers "is this the engine's own function", which is a
  // different and less useful question -- it produces false alarms where
  // nothing is wrong. It survives below only as an advisory ONE-TIME signal
  // for the one case identity cannot see: something that patched the prototype
  // BEFORE we loaded.
  const _rsqOriginalProto =
    typeof History === 'function' ? History.prototype.replaceState : null;
  let _rsqReported = false;

  /** True only for a genuine `[native code]` function; false if unverifiable. */
  function _rsqIsNativeFunction(fn) {
    try {
      return (
        Function.prototype.toString.call(fn).indexOf('[native code]') !== -1
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Announce, ONCE per page, that the APO-bypass guarantee has been lost.
   *
   * Once, because this sits on a path that runs per option selection and a
   * repeating warning would be its own performance problem. Dev-gated console
   * only: no event bus, no conductor -- core-utils is Phase 0 and must stay
   * dependency-free. The regression it reports is invisible by construction
   * (the write still succeeds; only its cost changes), so without this there is
   * nothing anywhere that would ever surface it.
   */
  function _rsqReportRegression(reason) {
    if (_rsqReported) return;
    _rsqReported = true;
    isDevelopment() &&
      console.warn(
        '[Utils] replaceStateQuietly fell back to the history INSTANCE: ' +
          reason +
          '. MageWorx APO will now re-parse the whole PDP on every ' +
          'configurator URL write. See the CLAUDE.md footgun "The Configurator ' +
          "'Leak' Was MageWorx APO Re-Parsing the Whole PDP\"."
      );
  }

  /**
   * ONE-TIME advisory: something patched the prototype BEFORE Phase 0 loaded.
   * Identity-since-load cannot see that case, so this is the only signal for
   * it. Advisory only -- it never affects the return value, because a
   * non-native prototype is normal in jsdom and under a polyfill.
   */
  function _rsqAuditOriginalPrototype() {
    if (
      _rsqOriginalProto &&
      !_rsqIsNativeFunction(_rsqOriginalProto) &&
      isDevelopment()
    ) {
      console.warn(
        '[Utils] History.prototype.replaceState was already non-native when ' +
          'core-utils loaded. Expected in jsdom/polyfilled environments; in a ' +
          'real browser it means something patched the prototype before us, ' +
          'and the APO bypass cannot be guaranteed.'
      );
    }
  }

  // Dev-only logging helper
  function isDevelopment() {
    if (typeof window === 'undefined' || !window.location) {
      return false;
    }
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.local') ||
      hostname.includes('ngrok')
    );
  }

  /**
   * The only two strings the price label is ever allowed to be.
   *
   * Frozen and module-scoped so every surface that renders a configurator
   * price — main row, sidebar/rail, gallery bar, traveling-preview chip and
   * the Summary total — spells them identically. They were five separate
   * hardcoded literals across three JS modules before this.
   */
  const PRICE_LABELS = Object.freeze({
    BASE: 'Starting at',
    CONFIGURED: 'Your Price',
  });

  // Named so methods can reach siblings without `this` (callers hold bare refs).
  const utils = (window.MurphyDoorConfigurator.utils = {
    /* Development Environment Detection */
    isDevelopment: isDevelopment,

    /* Price label vocabulary — see resolvePriceLabel() for the choice rule */
    PRICE_LABELS: PRICE_LABELS,

    /* Formatting Methods */
    /**
     * ⚡ ARCHITECTURAL VIOLATION: This method performs runtime display formatting
     * ALL display formatting MUST be pre-extracted in PPD Layer 1
     * Use variant.displayFormatting.baseDisplay instead
     * @throws {Error} Always throws to enforce PPD-first architecture
     */
    formatPriceWithTitle(title, price) {
      throw new Error(
        '🔥 CRITICAL ARCHITECTURAL VIOLATION: Runtime display formatting is PROHIBITED in PPD-First architecture!\n' +
          '✅ CORRECT PATTERN: Use variant.displayFormatting.baseDisplay (pre-extracted in PPD Layer 1)\n' +
          '❌ LEGACY PATTERN: formatPriceWithTitle() runtime string concatenation\n' +
          'This is a PPD-First architecture violation - all formatting must happen during bootstrap Phase 1.\n' +
          `Attempted to format: "${title}" with price: "${price}"`
      );
    },

    /**
     * Drop a trailing ".00" from an already-formatted price string.
     *
     * Luxury pricing convention: "$1,750" reads as a commissioned piece,
     * "$1,750.00" reads as a shelf SKU. Trailing zeros also make the number
     * *feel* larger — perceived magnitude tracks the visual length of the
     * string, not only its value.
     *
     * 🚨 WHOLE DOLLARS ONLY — THIS MUST NEVER ROUND. 60 of the 1,214 variant
     * prices in the PPD carry real non-zero cents (add-on hardware at $20.90,
     * $22.60, $92.50 ...), so a configuration can legitimately total
     * $1,770.90. Rendering that as "$1,771" would put a number on screen that
     * the cart never charges. The regex is anchored to a literal ".00" at the
     * END of the string precisely so real cents survive untouched — do NOT
     * "simplify" this into a rounding call or `maximumFractionDigits: 0`.
     *
     * Operates on the FORMATTED string rather than the number so that every
     * surface (main total, category button, Summary row, finish tiles) shares
     * one rule and cannot drift apart — the same consolidation the leading "$"
     * already went through, for the same reason.
     *
     * @param {string} formatted - e.g. "$1,750.00", "1,750.00", "$1,770.90"
     * @returns {string} e.g. "$1,750", "1,750", "$1,770.90" (unchanged)
     */
    dropWholeDollarCents(formatted) {
      if (typeof formatted !== 'string') return formatted;
      return formatted.replace(/\.00$/, '');
    },

    /**
     * CONSTANTS.DERIVED_PRODUCT_RULES matcher (pure). Returns the ONE product
     * whose display name (or title) is contained in `sourceLabel`, or null.
     *
     * "Standard Shelves" is contained in "Standard Shelves (Paint Grade)
     * (Single Door)", but "Lipped Shelves" is not, so Extra Shelves follows
     * the applied Shelf Style whatever material it is in. Zero or several
     * matches return null and the caller falls back rather than guessing.
     *
     * @param {Array<Object>} products - PPD products of the derived category
     * @param {string} sourceLabel - source selection's product label(s)
     * @returns {Object|null} the matched product
     */
    matchDerivedProduct(products, sourceLabel) {
      if (!Array.isArray(products) || !sourceLabel) return null;
      const norm = (text) =>
        String(text || '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      const label = norm(sourceLabel);
      if (!label) return null;
      const matches = products.filter((product) => {
        const key = norm(product && (product.display_name || product.title));
        return key && label.includes(key);
      });
      return matches.length === 1 ? matches[0] : null;
    },

    /**
     * An OPTION's price label: "$150", "$20.90", or a caller-chosen zeroLabel
     * ("(Included)") for a $0 option. The one formatter behind the Summary
     * row, the category caption and the slider tooltip, so a Width priced on
     * the slider can never read differently from the same Width in Summary.
     *
     * Accepts the PPD's pre-formatted dollar string ("1,190.00"), a bare
     * number, or an already-"$"-prefixed string. Empty/null in, '' out.
     *
     * @param {string|number} price
     * @param {{zeroLabel?: string|null}} [options]
     * @returns {string}
     */
    formatOptionPrice(price, { zeroLabel = null } = {}) {
      if (price === null || price === undefined) return '';
      const raw = String(price).trim();
      if (raw === '') return '';
      const numeric = parseFloat(raw.replace(/[$,]/g, ''));
      if (zeroLabel && Number.isFinite(numeric) && numeric === 0) {
        return zeroLabel;
      }
      return '$' + utils.dropWholeDollarCents(raw.replace(/^\$/, ''));
    },

    /**
     * A measurement slider's SHORT label (range ends, "Use default (X)"):
     *   "24 in. (Rough Opening 26 in.)" -> "24 in."     (Width/Height, unchanged)
     *   '8-1/4" Door | 6-1/2" Shelf'    -> "8-1/4 in."  (Door Depth)
     *   '12" Door | 10-1/4" Shelf'      -> "12 in."
     *
     * Three generic steps, each a no-op on a title that lacks its shape, so a
     * Width/Height title comes out byte-identical to the pre-2026-09-28
     * inline simplifyTitle it replaced (md-config-core-data-pipeline.js
     * Layer 4.6): drop a "(Rough Opening ...)" suffix, drop a " | ..." second
     * dimension, then rewrite a LEADING inch-mark measurement in the "N in."
     * unit style the Width labels use (the word after it goes with it: the
     * slider's own category already names what is being measured).
     *
     * Phase 0 so the pipeline (which builds simplifiedTitle) and any UI module
     * share one rule. Non-string in -> returned unchanged.
     *
     * @param {string} title
     * @returns {string}
     */
    simplifyDimensionTitle: function simplifyDimensionTitle(title) {
      if (typeof title !== 'string') return title;
      let t = title;
      const ro = t.match(/^(.+?)\s*\(Rough Opening/i);
      if (ro) t = ro[1].trim();
      const pipe = t.indexOf(' | ');
      if (pipe !== -1) t = t.slice(0, pipe).trim();
      const inch = t.match(/^(\d+(?:[ -]\d+\/\d+)?(?:\.\d+)?)\s*["\u2033]/);
      return inch ? inch[1] + ' in.' : t;
    },

    /**
     * A measurement slider's FULL reading (the handle tooltip, aria-valuetext):
     *   '8-1/4" Door | 6-1/2" Shelf'    -> "8-1/4 in. door \u00b7 6-1/2 in. shelf"
     *   "24 in. (Rough Opening 26 in.)" -> unchanged (no " | " part)
     *
     * Derived from the TITLE's own "a | b" shape, never from a category id, so
     * the slider stays free of category literals. Each part gets the inch mark
     * rewritten as " in." and is lower-cased, then the parts are joined with a
     * middle dot.
     *
     * @param {string} title
     * @returns {string}
     */
    describeDimensionTitle: function describeDimensionTitle(title) {
      if (typeof title !== 'string' || title.indexOf(' | ') === -1) {
        return title;
      }
      return title
        .split(' | ')
        .map(function (part) {
          return part
            .trim()
            .replace(/(\d)\s*["\u2033]/g, '$1 in.')
            .toLowerCase();
        })
        .filter(Boolean)
        .join(' \u00b7 ');
    },

    /**
     * The two price labels, and the single rule for choosing between them.
     *
     * "Starting at" is a PROMISE: it tells the customer the number on screen
     * is a floor they will build up from. "Your Price" is a STATEMENT: this
     * is what your configuration costs. Showing the wrong one is not a
     * cosmetic slip — "Your Price $1,440" on an untouched door tells the
     * customer they are done when they have not begun, and "Starting at" over
     * a fully-configured $2,130 understates what the cart will charge.
     *
     * 🚨 THE LABEL IS DERIVED FROM PRICE, NOT FROM SELECTION COUNT. It used
     * to read `hasAnySelections() ? 'Your Price' : 'Starting at'`, which is
     * why this exists. Of the 20 categories on the Hidden Bookcase Door, five
     * (Locking Systems, Delivery, Smart Home Automations, Appearance
     * Upgrades, Bottom Cabinet Doors) resolve to a $0 "Default Title" variant
     * that is SELECTED without being an upcharge — and a restored `?config=`
     * permalink re-applies them on load. So the old predicate flipped to
     * "Your Price" at base price, before the customer had chosen anything
     * that cost money. Measured on dev 2026-09-22: base $1,440, label already
     * "Your Price". Comparing against base price is what makes those five
     * free selections correctly invisible to the label.
     *
     * WHY THIS LIVES IN PHASE 0 rather than inline in the price calculator:
     * three modules decide this label — the price calculator on every
     * recalculation, the conductor at bootstrap, and the ui-updater's
     * fallback — and before this helper each one hardcoded its own literal.
     * Same consolidation, and the same reason, as dropWholeDollarCents above.
     *
     * @param {number|string} totalPrice - configured total, dollars
     * @param {number|string} basePrice - base product price, dollars
     * @returns {string} PRICE_LABELS.CONFIGURED or PRICE_LABELS.BASE
     */
    resolvePriceLabel(totalPrice, basePrice) {
      const labels = PRICE_LABELS;

      // Tolerant coercion is LOAD-BEARING, not defensive padding.
      // getBasePrice() returns the PPD's pre-formatted dollar STRING
      // ("1,440.00") while calculateTotalPrice() returns a NUMBER (1440) --
      // see the note in md-config-bl-price-calculator.js parsePrice(). A bare
      // `1440 > "1,440.00"` coerces to NaN and is always false, which would
      // silently pin the label to "Starting at" forever.
      const toNumber = (value) =>
        typeof value === 'number'
          ? value
          : parseFloat(String(value).replace(/[,$]/g, ''));

      const total = toNumber(totalPrice);
      const base = toNumber(basePrice);

      // Unreadable input -> the promise, never the statement. "Starting at"
      // over a correct number is merely conservative; "Your Price" over a
      // number we could not verify is a quote we cannot stand behind.
      if (!Number.isFinite(total) || !Number.isFinite(base)) {
        return labels.BASE;
      }

      // Half a cent. Both sides are floating-point sums of parsed decimal
      // strings (add-on hardware ships real cents: $20.90, $92.50 ...), so an
      // exact !== would flip the label on binary-addition drift alone.
      // Deliberately abs(): a configuration that came in BELOW base is still
      // a configured price, and calling it "Starting at" would advertise a
      // floor underneath our own advertised floor.
      return Math.abs(total - base) > 0.005 ? labels.CONFIGURED : labels.BASE;
    },

    /**
     * Which side of the door a category is ABOUT -- 'front', 'back', or null.
     *
     * The configurator's gallery can show the door from the front or the back,
     * and some categories only make sense from one side: choosing "Back
     * Casing" is pointless while the customer is looking at the front. Daniel,
     * 2026-08-13: the apo_style category ORDER already clusters front-facing
     * categories together and back-facing ones together for exactly this
     * reason, "interleaving Front/Back/Front/Back forced a camera flip on
     * every single step" (see CATEGORY_ORDER in md-config-core-constants.js).
     * This helper is the other half of that decision -- the order groups the
     * flips, this decides which way to flip.
     *
     * 🚨 'Door Backing' RESOLVES TO 'back' AND THAT IS DELIBERATE. It is not
     * literally "Back "-prefixed, so a stricter startsWith('back ') rule would
     * silently drop it -- and Door Backing is the category this rule matters
     * MOST for, because every one of its tiles is a composite product (see
     * below). Both the constants comment and the live tab-discovery position
     * put it in the back cluster. Ryan, UAT 2026-09-17: "When the user clicks
     * any of the 'Door Backing' options, I expect the carousel to auto-toggle
     * to picture and back view", and separately "Back Casing / Back
     * Entablature are also supposed to switch it to the back view."
     *
     * WHY THIS LIVES IN PHASE 0 rather than staying inline in the conductor.
     * Three independent modules act on this question and, before this helper,
     * only ONE of them knew the answer:
     *
     *   md-config-core-conductor.js          had this rule, inline
     *   media-gallery.js                     forced 'front' after a cascade
     *   md-config-bl-image-layer-coordinator.js  toggled on image availability
     *
     * Measured on production and on the dev server (apo_style + click-apply,
     * wood applied, carousel parked on a photo slide, clicking Door Backing ->
     * Flat Backing), the three fought and the customer's own intent lost:
     *
     *   t+2    setView(back)  <- conductor, category preference. CORRECT.
     *   t+117  setView(front) <- media-gallery, cascade completed. STOMP 1.
     *   t+121  setView(back)  <- coordinator, Back Entablature is back-only.
     *   t+122  setView(front) <- coordinator, Front Casing is front-only. FINAL.
     *
     * Neither stomp consulted the category the customer had clicked, so the
     * back view flashed and snapped away. Order decided the winner: the last
     * cascade-applied category to finish rendering won the camera. One shared
     * rule plus a recorded intent (see the conductor's
     * `_recordCustomerViewIntent`) is what makes that resolvable at all.
     *
     * Pure, string-only, no DOM and no events -- Phase 0 must stay
     * dependency-free.
     *
     * @param {string} categoryId - e.g. 'Back Casing', 'Door Backing', 'Width'
     * @returns {'front'|'back'|null} null when the category is side-neutral
     */
    getCategoryPreferredView(categoryId) {
      if (typeof categoryId !== 'string') return null;
      const name = categoryId.toLowerCase();
      // 'back' is tested FIRST and the order is load-bearing: a hypothetical
      // "Front and Back Trim" must land on 'back', matching the conductor's
      // original if/else-if precedence this helper replaced byte-for-byte.
      if (name.includes('back')) return 'back';
      if (name.includes('front')) return 'front';
      return null;
    },

    /**
     * Escape a string for interpolation into HTML -- element content OR either
     * quote style of attribute.
     *
     * WHY THIS LIVES IN PHASE 0. Before this, the configurator carried SEVEN
     * hand-rolled copies of this function in THREE non-identical variants:
     *
     *   & < > " '   md-config-core-data-pipeline.js, ui-navigation-manager.js,
     *               and the three *-tooltip-initializer.js modules
     *   & < > "     md-config-ui-updater.js's method-scoped safeText()
     *   & < > ' "   a second, function-scoped escapeHTML() in ui-updater.js
     *
     * The middle one omits the apostrophe. That is NOT a live bug today --
     * every attribute it feeds is double-quoted, which was checked rather than
     * assumed -- but it is the exact shape of the stored-XSS fixed in
     * sections/dynamic-banner.liquid the same day: a value carrying a quote
     * closing an attribute early. A security primitive with three spellings
     * across seven copies WILL drift again, and the drift is invisible until
     * someone writes a single-quoted attribute.
     *
     * This escapes the full five-character set, so it is safe in every HTML
     * context and callers do not have to know which quote style they are in.
     * Phase 0 is the right home: pure function, no events, no DOM, no PPD --
     * reachable from Phase 1 and Phase 2 modules alike without a cross-phase
     * call, exactly like cdnOptimizedUrl() and dropWholeDollarCents().
     *
     * @param {*} value - Any value; non-strings are coerced, null/undefined -> ''
     * @returns {string} HTML-safe string
     */
    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    /**
     * "5-1/4 in. (Shelf Depth = 3-1/2 in.)" -> 5.25
     * "4-9/16 in."                          -> 4.5625
     * "8-1/4\" Door | 6-1/2\" Shelf"        -> 8.25
     * "Custom (Our Concierge Will Call You)" -> null  <-- REAL catalog value
     *
     * PROMOTED FROM md-config-bl-dimension-calculator.js (Phase 2) TO PHASE 0 so
     * a Phase 2 UI module can call it without a peer-to-peer BL call — CCP Rule
     * #2's exception is "Utility module access (md-config-core-utils.js shared
     * functions)", not "any other Phase 2 module's exports". Kept as a NAMED
     * function expression (not ES6 shorthand) so the existing brace-matching
     * test extraction technique in tests/assets/jamb-depth-restructure.test.js
     * keeps working unmodified when pointed at this file.
     *
     * dimension-calculator.js's own parseFractionalInches is now a thin
     * delegate to this implementation — do not let the two drift; this is the
     * single source of truth.
     */
    parseFractionalInches: function parseFractionalInches(title) {
      if (typeof title !== 'string') return null;
      // 🚨 TWO UNIT SPELLINGS ARE LIVE, AND THEY DIFFER BY CATEGORY.
      //   Jamb Depth : '4-9/16 in.'                  <- the `in.` form
      //   Door Depth : '8-1/4" Door | 6-1/2" Shelf'  <- the inch-mark form
      const m = title.match(/^(\d+)(?:-(\d+)\/(\d+))?\s*(?:in\.|["″])/i);
      if (!m) return null;
      const whole = Number(m[1]);
      if (!Number.isFinite(whole)) return null;
      if (m[2] && m[3]) {
        const num = Number(m[2]);
        const den = Number(m[3]);
        if (!den) return whole;
        return whole + num / den;
      }
      return whole;
    },

    /**
     * Decompose a non-negative decimal value into a reduced {whole, num, den}
     * fraction at the given denominator (default sixteenths). Rounds the
     * TOTAL value to the nearest denominator unit first, then splits into
     * whole/remainder -- this is what lets a value that rounds up to the next
     * whole number (e.g. 37.99 at sixteenths) resolve correctly with no
     * separate carry-correction step, unlike rounding the fractional part
     * after already flooring the whole part.
     *
     * Shared by formatInches (below, for display strings) and by any Phase 2
     * module needing the structured triple directly (e.g. a stepper UI
     * comparing/stepping fraction values) — single source of truth for the
     * reduction algorithm so the two can never silently drift apart.
     *
     * @param {number} value - non-negative decimal inches (callers handle sign)
     * @param {number} [denominator] - defaults to 16
     * @returns {{whole: number, num: number, den: number}|null}
     */
    decimalInchesToFraction: function decimalInchesToFraction(value, denominator) {
      if (!Number.isFinite(value)) return null;
      const den0 = denominator || 16;
      const totalUnits = Math.round(Math.abs(value) * den0);
      const whole = Math.floor(totalUnits / den0);
      let num = totalUnits - whole * den0;
      let den = den0;
      while (num > 0 && num % 2 === 0 && den % 2 === 0) {
        num /= 2;
        den /= 2;
      }
      return { whole, num, den };
    },

    /**
     * 40.5 -> '40 1/2″',  38 -> '38″',  4.5625 -> '4 9/16″'.
     *
     * PROMOTED FROM md-config-bl-dimension-calculator.js (Phase 2) — see
     * parseFractionalInches above for why. SIXTEENTHS, NOT EIGHTHS — a coarser
     * denominator silently misreports a real catalog value (4-9/16" rounds to
     * 4-5/8" under 1/8ths). Fraction is reduced after rounding.
     *
     * dimension-calculator.js's own formatInches is now a thin delegate to
     * this implementation — single source of truth.
     */
    formatInches: function formatInches(value, denominator) {
      if (!Number.isFinite(value)) return null;
      const neg = value < 0;
      const { whole, num, den } = this.decimalInchesToFraction(value, denominator);
      const sign = neg ? '-' : '';
      if (num === 0) return `${sign}${whole}″`;
      if (whole === 0) return `${sign}${num}/${den}″`;
      return `${sign}${whole} ${num}/${den}″`;
    },

    /**
     * Engineering's "-1 RULE" (sheet "Door Measurement Calculator", R. Cooley,
     * rev. 2026-09-25), scope FMD2 SINGLE door:
     *
     *   (In-Swing AND jamb > 4-9/16in) OR (Out-Swing AND depth >= 10in)
     *
     * Strict `>` on jamb (4-9/16 itself is NOT wide), `>=` on depth (10 IS).
     * ctx: { swing: 'Inswing'|'Outswing', jambIn, depthIn }.
     *
     * PROMOTED FROM md-config-bl-dimension-calculator.js (Phase 2) on
     * 2026-09-25 so the space calculator's in-swing pass-through (sheet B24,
     * which subtracts the casing width this rule selects) reads the SAME
     * predicate without a peer-to-peer BL call (CCP Rule #2 exception:
     * "Utility module access"). dimension-calculator.js's meetsMinusOneRule is
     * a thin delegate -- single source of truth. The Fluted exemption is NOT
     * here: it changes the casing consequence only (trimProfiles
     * `wideRuleExempt`), not whether the rule is met.
     */
    meetsMinusOneRule: function meetsMinusOneRule(ctx) {
      if (!ctx) return false;
      const JAMB_THRESHOLD_IN = 4.5625; // 4-9/16in, standard jamb
      const DEPTH_THRESHOLD_IN = 10; // 10in
      return !!(
        (ctx.swing === 'Inswing' &&
          Number.isFinite(ctx.jambIn) &&
          ctx.jambIn > JAMB_THRESHOLD_IN) ||
        (ctx.swing === 'Outswing' &&
          Number.isFinite(ctx.depthIn) &&
          ctx.depthIn >= DEPTH_THRESHOLD_IN)
      );
    },

    /* Validation Methods */
    validateDataStructure(structure, requiredKeys) {
      return requiredKeys.every((key) => structure.has(key));
    },

    /**
     * Reflect a forbidden/unavailable state to assistive tech (WCAG 4.1.2).
     *
     * The `.forbidden-disabled` class greys an option and the event-handler blocks its
     * click, but a screen reader still announced an ordinary available button — the
     * customer pressed Enter and got silence with no explanation.
     *
     * Only touches elements that are actually exposed as controls. The forbidden
     * selectors (`[data-variant-id]`, falling back to `[data-product-id]`) are broad
     * enough to match summary rows and gallery nodes, and `aria-disabled` on a
     * non-interactive div is noise at best and misleading at worst.
     *
     * Native `<button>`/`<input>`/`<select>` are skipped: their own `disabled`
     * property already carries the semantics, and setting both invites the two to
     * disagree (see the Apply button, which uses `disabled` and must stay that way).
     *
     * Clearing REMOVES the attribute rather than setting "false" — an element that is
     * no longer forbidden should look untouched, and `aria-disabled="false"` is a
     * different announcement from having no attribute at all.
     *
     * @param {Element} element - candidate element, may be non-interactive
     * @param {boolean} isDisabled - true to mark unavailable, false to clear
     * @returns {boolean} whether the element was treated as a control
     */
    setAriaDisabled(element, isDisabled) {
      if (!element || typeof element.setAttribute !== 'function') return false;

      const tag = (element.tagName || '').toLowerCase();
      if (
        tag === 'button' ||
        tag === 'input' ||
        tag === 'select' ||
        tag === 'textarea'
      ) {
        return false;
      }

      if (element.getAttribute('role') !== 'button') return false;

      if (isDisabled) {
        element.setAttribute('aria-disabled', 'true');
      } else {
        element.removeAttribute('aria-disabled');
      }
      return true;
    },

    /* DOM Interaction Methods */
    simulateClick(element) {
      if (element) {
        // Check if this is a category button and get its ID if it is
        const isCategoryButton = element.classList.contains(
          'murphy-door-configurator__category-button'
        );
        const categoryId = isCategoryButton
          ? element.getAttribute('data-category')
          : null;

        // Create custom data for the event
        const customData = categoryId
          ? { categoryId, programmatic: true }
          : undefined;

        // Create a more detailed click event with the right properties
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });

        // Add custom data to the event
        if (customData) {
          // Store the data on the event object
          Object.defineProperty(clickEvent, 'programmaticData', {
            value: customData,
            writable: false,
          });
        }

        // Force a browser reflow to ensure the styles are applied
        element.offsetHeight;

        // Dispatch the click event to trigger navigation manager handlers
        // Navigation manager now properly handles all visual state classes
        element.dispatchEvent(clickEvent);
      }
    },

    /**
     * Gets a category element by its ID
     * @param {string} categoryId - The ID of the category to retrieve
     * @returns {HTMLElement|null} - The category element or null if not found
     */
    getCategoryElement(categoryId) {
      if (!categoryId) {
        isDevelopment() &&
          console.error('utils.getCategoryElement: categoryId is required');
        return null;
      }
      if (!window.MurphyDoorConfigurator.main) {
        isDevelopment() &&
          console.error(
            'utils.getCategoryElement: main module not initialized'
          );
        return null;
      }
      return window.MurphyDoorConfigurator.main.getCategoryElement(categoryId);
    },

    /**
     * Build a canonical preference key for O(1) material filter state lookups.
     *
     * SINGLE SOURCE OF TRUTH for preference key format used by:
     * - md-config-bl-material-preference-coordinator.js (runtime lookup)
     * - md-config-support-material-filter-builder.js (bootstrap pre-computation)
     *
     * Key format:
     *   - Wood only:     'wood|{woodSku}'
     *   - Finish only:   'finish|{finishSku}'
     *   - Wood + Finish: 'wood|{woodSku}|finish|{finishSku}'
     *   - No preference:  null
     *
     * @param {Object} preferences - Material preferences object
     * @param {boolean} preferences.woodApplied - Whether wood is applied
     * @param {string|null} preferences.appliedWoodSku - Wood SKU or null
     * @param {boolean} preferences.finishApplied - Whether finish is applied
     * @param {string|null} preferences.appliedFinishSku - Finish SKU or null
     * @returns {string|null} Canonical preference key, or null if no preference applied
     */
    buildMaterialPreferenceKey(preferences) {
      if (!preferences) return null;

      const hasWood = preferences.woodApplied && preferences.appliedWoodSku;
      const hasFinish =
        preferences.finishApplied && preferences.appliedFinishSku;

      if (hasWood && hasFinish) {
        return (
          'wood|' +
          preferences.appliedWoodSku +
          '|finish|' +
          preferences.appliedFinishSku
        );
      } else if (hasWood) {
        return 'wood|' + preferences.appliedWoodSku;
      } else if (hasFinish) {
        return 'finish|' + preferences.appliedFinishSku;
      }

      return null;
    },

    /**
     * Compute compatible and incompatible variant IDs for a product based on a filtered subset.
     *
     * SINGLE SOURCE OF TRUTH for variant compatibility logic used by:
     * - md-config-support-material-filter-builder.js (bootstrap pre-computation in _buildFilterStateMaps)
     * - md-config-bl-material-preference-coordinator.js (runtime fallback in _computeFilterStateFallback)
     *
     * A variant is COMPATIBLE if:
     *   1. It appears in the filteredVariants set (matches the active material preference), OR
     *   2. It is material-agnostic (has neither wood_sku nor finish_sku)
     *
     * All other variants are INCOMPATIBLE.
     *
     * @param {Array} allProductVariants - All variants for a given product
     * @param {Array} filteredProductVariants - The subset of variants matching a material preference
     * @returns {{compatible: string[], incompatible: string[], all: string[]}}
     */
    computeVariantCompatibility(allProductVariants, filteredProductVariants) {
      const compatible = [];
      const incompatible = [];
      const all = [];
      const filteredIds = new Set(
        filteredProductVariants.map(function (v) {
          return v.id;
        })
      );

      allProductVariants.forEach(function (variant) {
        all.push(variant.id);
        const isMaterialAgnostic = !variant.wood_sku && !variant.finish_sku;
        if (filteredIds.has(variant.id) || isMaterialAgnostic) {
          compatible.push(variant.id);
        } else {
          incompatible.push(variant.id);
        }
      });

      return { compatible, incompatible, all };
    },

    /**
     * Centralized system error handler - SINGLE publication point for SYSTEM_ERROR
     * Pattern #15: Single Publication Point (DRY compliance)
     *
     * ALL modules MUST use this utility instead of directly publishing SYSTEM_ERROR.
     * This ensures consistent error payload structure, centralized logging, and
     * a single point of change for error handling behavior.
     *
     * @param {Object} errorContext - Error context object
     * @param {string} [errorContext.type] - Error type (e.g., 'DependencyError', 'ValidationError')
     * @param {Error|string} errorContext.error - The error object or error message string
     * @param {string} [errorContext.message] - Human-readable error message
     * @param {string} errorContext.source - Module/function that triggered the error
     * @param {boolean} [errorContext.recoverable=true] - Whether error is recoverable
     * @param {string} [errorContext.impact='medium'] - Impact level: 'low', 'medium', 'critical'
     * @param {Object} [errorContext.details={}] - Additional context details
     * @returns {boolean} True if error event was published, false otherwise
     */
    /**
     * Construct a Shopify CDN image URL with a specific width parameter.
     * Strips any existing ?width= param and sets the requested width.
     * Returns original URL unchanged if it is not a Shopify CDN URL or is malformed.
     *
     * Pure utility — no side effects, no events, no PPD access.
     *
     * @param {string} url - The original image URL
     * @param {number} width - Desired pixel width
     * @returns {string} URL with width param applied
     */
    /**
     * Calculate the optimal CDN width for thumbnails based on device pixel ratio.
     * Centralises the DPR-aware sizing formula used across UI modules.
     *
     * @param {number} [baseWidth=100] - Base thumbnail width at 1× DPR (CSS pixels)
     * @param {number} [maxWidth=240] - Maximum CDN width to request (caps DPR=3+ devices)
     * @returns {number} Pixel width to request from the CDN
     *
     * @warning MAINTENANCE: This function's logic is duplicated as a load-order-safe
     * fallback in md-config-ui-navigation-manager.js and md-config-ui-updater.js.
     * Any changes to the calculation MUST be synchronized in those fallbacks.
     */
    getCdnThumbnailWidth: function (baseWidth, maxWidth) {
      const base = baseWidth || 100;
      const cap = maxWidth || 240;
      return Math.min(Math.ceil(base * (window.devicePixelRatio || 1)), cap);
    },

    cdnOptimizedUrl: function (url, width) {
      if (!url || typeof url !== 'string' || !width) return url;
      if (
        !url.includes('cdn.shopify.com') &&
        !url.includes('myshopify.com') &&
        !url.includes('/cdn/shop/')
      )
        return url;
      try {
        // Pass window.location.href as base so protocol-relative URLs (//domain/path) resolve correctly.
        // new URL() alone throws on protocol-relative URLs; base is ignored for absolute URLs.
        const urlObj = new URL(url, window.location.href);
        urlObj.searchParams.set('width', String(Math.round(width)));
        return urlObj.toString();
      } catch (e) {
        return url; // Graceful fallback for malformed URLs
      }
    },

    /**
     * Collect the APPLIED image layers of one gallery view, bottom to top.
     *
     * SINGLE SOURCE OF TRUTH for "which <img> in the configurator gallery is
     * part of the door the customer has actually chosen, and in what order do
     * they stack". Consumers:
     *   - md-config-ui-image-export-manager.js  (download export + the cart
     *     thumbnail composite) -- delegates here, with its own inline copy
     *     retained only as a load-order fallback.
     *   - md-config-ui-ready-badge.js           (the "Your door is ready!"
     *     picture) -- degrades to no picture if this is unavailable.
     *
     * It lives in Phase 0 because a DIVERGENT copy of a layer-ordering rule is
     * the two-builders-that-disagree shape that produced the composite
     * group-id P0: two implementations that agree today, one merchandising or
     * markup change apart from disagreeing silently.
     *
     * Pure: reads the DOM, returns data. No events, no PPD, no conductor.
     *
     * Rules, each of which exists for a measured reason:
     * - data-layer-type="browsing" is a PREVIEW of something not yet applied.
     * - INLINE display:none / opacity:0 on the layer div means individually
     *   hidden. Inline, never computed: the back-view container itself carries
     *   display:none as the inactive view, so a computed read would inherit
     *   that and falsely drop every back layer.
     * - A COMPLETE 1x1 is the shared fully transparent "None" file, which the
     *   gallery deliberately requests at width=1 (media-gallery.js
     *   BLANK_LAYER_WIDTH) because its master is a ~16 MiB transparent PNG.
     *   Only a COMPLETE 1x1 is skipped: an image still loading also reports
     *   small/zero and must stay in.
     * - Sort by data-z-index, falling back to inline z-index. Since PR #1822
     *   the gallery also stamps data-stack-order to break z-index TIES; this
     *   returns DOM order within a tie, which is the order the gallery itself
     *   painted them.
     *
     * @param {string} viewType - 'front' or 'back'
     * @returns {Array<{src: string, zIndex: number}>} bottom-to-top, bare srcs
     *          (CDN query stripped) so each caller can request its own width
     */
    collectAppliedLayerDescriptors: function (viewType) {
      const viewSelector =
        viewType === 'front'
          ? '.murphy-door-configurator__front-view'
          : '.murphy-door-configurator__back-view';

      const container = document.querySelector(viewSelector);
      if (!container) return [];

      return Array.from(container.querySelectorAll('img'))
        .filter(function (img) {
          if (!img.src || img.src === '' || img.src === window.location.href) {
            return false;
          }
          // `complete &&` is LOAD-BEARING -- see the "COMPLETE 1x1" rule in
          // this function's doc block. Do not relax to `naturalWidth <= 1`:
          // an image still loading also reports 0 and must stay in.
          if (
            img.complete &&
            img.naturalWidth === 1 &&
            img.naturalHeight === 1
          ) {
            return false;
          }
          const layerDiv = img.closest(
            '.murphy-door-configurator__category-layer'
          );
          if (layerDiv) {
            if (layerDiv.getAttribute('data-layer-type') === 'browsing') {
              return false;
            }
            // INLINE style, never getComputedStyle -- see the "INLINE
            // display:none" rule in this function's doc block. The back-view
            // container is itself display:none as the inactive view, so a
            // computed read inherits that and drops every back layer.
            if (layerDiv.style.display === 'none') return false;
            if (parseFloat(layerDiv.style.opacity) === 0) return false;
          }
          return true;
        })
        .map(function (img) {
          const src = img.src.split('?')[0];
          const layerDiv = img.closest(
            '.murphy-door-configurator__category-layer'
          );
          let zIndex = 0;
          if (layerDiv) {
            const dataZIndex = layerDiv.getAttribute('data-z-index');
            zIndex = dataZIndex
              ? parseInt(dataZIndex, 10)
              : parseInt(layerDiv.style.zIndex || '0', 10);
          } else {
            // Base image -- directly under the view container.
            const viewDiv = img.closest('[data-z-index]');
            if (viewDiv) {
              zIndex = parseInt(
                viewDiv.getAttribute('data-z-index') || '0',
                10
              );
            }
          }
          return { src: src, zIndex: zIndex };
        })
        .sort(function (a, b) {
          return a.zIndex - b.zIndex;
        });
    },

    /**
     * Widths offered in a thumbnail `srcset`, in CSS pixels.
     *
     * A configurator tile renders at most 100 CSS px wide -- `--murphy-door-config-thumbnail-size`
     * is 100px, and the mobile rule is `width: 20vw` with `max-width` pinned to that same token.
     * So 100/200/300 covers DPR 1/2/3 exactly, with nothing above 3x because no shipping device
     * needs it and every extra candidate is bytes the CDN has to generate.
     */
    THUMBNAIL_SRCSET_WIDTHS: [100, 200, 300],

    /**
     * Build a `srcset` string for a Shopify CDN image.
     *
     * Returns '' (not a partial string) for anything cdnOptimizedUrl() would decline to
     * rewrite -- a non-CDN URL, an empty URL, a malformed one. Callers can then simply omit
     * the attribute, because an `srcset` whose candidates are all the SAME unresized URL is
     * worse than no srcset at all: the browser still has to pick one, and every candidate is
     * the full-size original.
     *
     * WHY THIS EXISTS (and why it is not just a nicety): tile HTML is cached in IndexedDB
     * (`murphy_door_ppd_cache`, PPD layer 5). The pre-existing approach picked ONE width from
     * `getCdnThumbnailWidth()` using `window.devicePixelRatio` AT BUILD TIME and baked it into
     * the cached markup. A `srcset` moves the choice to the browser at paint time, which makes
     * the cached HTML DPR-portable instead of DPR-stamped.
     *
     * @param {string} url - Original image URL
     * @param {number[]} [widths] - Candidate widths; defaults to THUMBNAIL_SRCSET_WIDTHS
     * @returns {string} `"url?width=100 100w, ..."`, or '' when not usable
     */
    cdnSrcset: function (url, widths) {
      if (!url || typeof url !== 'string') return '';
      const list =
        Array.isArray(widths) && widths.length
          ? widths
          : this.THUMBNAIL_SRCSET_WIDTHS;
      const parts = [];
      for (let i = 0; i < list.length; i++) {
        const w = Math.round(list[i]);
        if (!w || w < 1) continue;
        const sized = this.cdnOptimizedUrl(url, w);
        // cdnOptimizedUrl returns the input unchanged when it cannot resize.
        // Emitting `original 100w, original 200w` would lie to the browser.
        if (sized === url) return '';
        parts.push(sized + ' ' + w + 'w');
      }
      return parts.length ? parts.join(', ') : '';
    },

    /**
     * Assemble a configurator thumbnail `<img>`.
     *
     * 🚨 `src` MUST REMAIN THE FIRST ATTRIBUTE. `applyBubbleUpTransformations()` in
     * md-config-ui-updater.js rewrites a tile's image by matching the literal
     * `<img\s+src="` and capturing the value. Moving `src` behind `srcset` makes that
     * regex silently stop matching -- no error, hybrid tiles just quietly keep the
     * wrong photo. Tests in tests/assets/thumbnail-srcset.test.js pin this ordering.
     *
     * @param {Object} opts
     * @param {string} opts.src - Already CDN-sized image URL
     * @param {string} [opts.srcsetFrom] - ORIGINAL (unsized) URL to derive srcset from
     * @param {string} [opts.alt] - Alt text; escaped here, callers must NOT pre-escape
     * @param {number} [opts.width=100]
     * @param {number} [opts.height=100]
     * @param {boolean} [opts.selfClosing=false] - Emit `/>` to match an existing call site
     * @returns {string} `<img ...>` markup
     */
    thumbnailImgHtml: function (opts) {
      const o = opts || {};
      const esc = this.escapeHtml.bind(this);
      const srcset = o.srcsetFrom ? this.cdnSrcset(o.srcsetFrom) : '';
      return (
        '<img src="' +
        esc(o.src || '') +
        '"' +
        (srcset ? ' srcset="' + esc(srcset) + '" sizes="100px"' : '') +
        ' alt="' +
        esc(o.alt || '') +
        '" width="' +
        (o.width || 100) +
        '" height="' +
        (o.height || 100) +
        '" loading="lazy"' +
        (o.selfClosing ? ' />' : '>')
      );
    },

    /**
     * Compute rush shipping time from a raw base shipping time range.
     *
     * Pure math utility — no PPD or event bus access — making it safe to call from
     * any module (navigation-manager, ui-updater, etc.) without CCP concerns.
     * Centralised here to ensure a single source of truth for the rush time
     * reduction business rule (DRY).
     *
     * Math: rushLow = max(1, baseLow - 1), rushHigh = baseHigh - 1
     *   - If rushHigh < 1: hidden = true (base is ~1 Week, rush not meaningful)
     *   - If rushLow === 1 && rushHigh === 1: text = "~1 Week"
     *   - Otherwise: text = "{rushLow} - {rushHigh} Week(s)"
     *
     * @param {{ low: number, high: number }|null} rawTimeRange - Base shipping time range
     * @returns {{ text: string, hidden: boolean }} Rush time text and whether thumbnail should be hidden
     */
    computeRushShippingTime: function (rawTimeRange) {
      if (
        !rawTimeRange ||
        rawTimeRange.low == null ||
        rawTimeRange.high == null
      ) {
        return { text: '', hidden: false };
      }
      const baseLow = rawTimeRange.low;
      const baseHigh = rawTimeRange.high;
      const rushHigh = baseHigh - 1;
      if (rushHigh < 1) {
        return { text: '', hidden: true };
      }
      const rushLow = Math.max(1, baseLow - 1);
      if (rushLow === 1 && rushHigh === 1) {
        return { text: '~1 Week', hidden: false };
      }
      return {
        // En dash per Brand Style Guide 2024 p.26 (see formatShippingTime).
        text: rushLow + ' – ' + rushHigh + ' Week' + (rushHigh > 1 ? 's' : ''),
        hidden: false,
      };
    },

    // =========================================================================
    // LEAD TIME: single source of truth = the product's `lead_time_category`
    // metaobject (custom.lead_time_category), piped in from
    // sections/md-configurator.liquid as productData.lead_time_category:
    //   { handle, unfinished: {low, high}, finished: {low, high} }  (integer WEEKS)
    // Pure helpers (no PPD / bus access) shared by the data pipeline (Layer 4),
    // the shipping calculator and the UI updater's arrival dates.
    // =========================================================================

    /**
     * Normalise the piped metaobject into usable week ranges.
     * The `finished` range is null when the category has none (e.g. RTA kits).
     *
     * @param {Object|null} raw - productData.lead_time_category
     * @returns {{ handle: string|null, unfinished: {low:number,high:number}, finished: {low:number,high:number}|null }|null}
     *   null when no metaobject is linked or its unfinished range is unusable
     *   (callers then fall back to the legacy production_time metafields).
     */
    resolveLeadTimeCategory: function (raw) {
      const toRange = (r) => {
        if (!r || r.low == null || r.high == null) return null;
        const low = Number(r.low);
        const high = Number(r.high);
        if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
        if (low <= 0 || high <= 0) return null;
        return { low: Math.min(low, high), high: Math.max(low, high) };
      };
      if (!raw || typeof raw !== 'object') return null;
      const unfinished = toRange(raw.unfinished);
      if (!unfinished) return null;
      return {
        handle: raw.handle || null,
        unfinished: unfinished,
        finished: toRange(raw.finished),
      };
    },

    /**
     * Classify a Wood & Finish variant for lead-time purposes.
     *  - 'unfinished': the variant's finish is Unfinished (sku UN), OR the
     *    product offers no Unfinished option at all (TFL laminate is
     *    factory-surfaced, not painted/stained in-house; today it runs on the
     *    base unfinished schedule, so that is preserved).
     *  - 'finished': any paint or stain finish on a product that also sells
     *    Unfinished (Paint Grade Black/White/Primer, veneer stains...).
     *  - null: no finish data, so not a finish choice.
     *
     * @param {Object} product - raw add-on product ({ finishes: [{finish, sku}] })
     * @param {Object} variant - raw add-on variant ({ finishes: [{finish, sku}] })
     * @returns {'finished'|'unfinished'|null}
     */
    classifyFinishLeadTime: function (product, variant) {
      const isUnfinished = (f) =>
        !!f &&
        (String(f.sku || '').toUpperCase() === 'UN' ||
          String(f.finish || '').trim().toLowerCase() === 'unfinished');
      const variantFinishes = Array.isArray(variant && variant.finishes)
        ? variant.finishes.filter(Boolean)
        : [];
      if (variantFinishes.length === 0) return null;
      const productFinishes = Array.isArray(product && product.finishes)
        ? product.finishes
        : [];
      if (!productFinishes.some(isUnfinished)) return 'unfinished';
      return variantFinishes.every(isUnfinished) ? 'unfinished' : 'finished';
    },

    /**
     * Add N business days (Mon-Fri) to a date. Returns a NEW Date.
     * @param {Date} date
     * @param {number} days - non-negative integer
     * @returns {Date}
     */
    addBusinessDays: function (date, days) {
      const d = new Date(date.getTime());
      let remaining = Math.max(0, Math.floor(Number(days) || 0));
      while (remaining > 0) {
        d.setDate(d.getDate() + 1);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) remaining--;
      }
      return d;
    },

    /**
     * Estimated arrival window = today + production weeks + carrier transit
     * business days. Low bound pairs the low production week with the low
     * transit day; high with high.
     *
     * @param {{low:number, high:number}} productionWeeks
     * @param {{low:number, high:number}} transitDays - business days
     * @param {Date} [now]
     * @returns {{ low: Date, high: Date }|null}
     */
    computeArrivalWindow: function (productionWeeks, transitDays, now) {
      if (
        !productionWeeks ||
        !Number.isFinite(productionWeeks.low) ||
        !Number.isFinite(productionWeeks.high)
      ) {
        return null;
      }
      const base = now instanceof Date ? now : new Date();
      const tLow = Math.max(0, Number(transitDays && transitDays.low) || 0);
      const tHigh = Math.max(tLow, Number(transitDays && transitDays.high) || 0);
      const lowShip = new Date(base.getTime());
      lowShip.setDate(lowShip.getDate() + productionWeeks.low * 7);
      const highShip = new Date(base.getTime());
      highShip.setDate(highShip.getDate() + productionWeeks.high * 7);
      return {
        low: utils.addBusinessDays(lowShip, tLow),
        high: utils.addBusinessDays(highShip, tHigh),
      };
    },

    handleSystemError: function (errorContext) {
      const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
      const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

      // Validate required fields
      if (!errorContext || !errorContext.source) {
        isDevelopment() &&
          console.error(
            'UTILS: handleSystemError called with invalid context',
            errorContext
          );
        return false;
      }

      // Normalize error - accept Error objects or strings
      const errorObj = errorContext.error;
      let errorMessage = '';
      let errorStack = '';

      if (errorObj instanceof Error) {
        errorMessage = errorObj.message || 'Unknown error';
        errorStack = errorObj.stack || '';
      } else if (typeof errorObj === 'string') {
        errorMessage = errorObj;
      } else {
        errorMessage = errorContext.message || 'Unknown error';
      }

      // Build standardized error payload
      const errorPayload = {
        type: errorContext.type || 'UnknownError',
        error: errorObj || new Error(errorMessage),
        message: errorContext.message || errorMessage,
        stack: errorStack,
        source: errorContext.source,
        timestamp: Date.now(),
        recoverable: errorContext.recoverable !== false,
        impact: errorContext.impact || 'medium',
        details: errorContext.details || {},
      };

      // Log to console in development
      if (isDevelopment()) {
        console.error(
          '[SYSTEM ERROR] [' +
            errorPayload.source +
            ']: ' +
            errorPayload.message
        );
        console.error('   Type:', errorPayload.type);
        console.error('   Impact:', errorPayload.impact);
        console.error('   Recoverable:', errorPayload.recoverable);
        if (errorPayload.stack) {
          console.error('   Stack:', errorPayload.stack);
        }
        if (Object.keys(errorPayload.details).length > 0) {
          console.error('   Details:', errorPayload.details);
        }
      }

      // Publish SYSTEM_ERROR event (single publication point)
      // Pattern #18: Freeze payload to prevent subscriber mutations
      if (CoreEventBus && CONSTANTS && CONSTANTS.EVENTS) {
        CoreEventBus.publish(
          CONSTANTS.EVENTS.SYSTEM_ERROR,
          Object.freeze(errorPayload)
        );
        return true;
      } else {
        isDevelopment() &&
          console.error(
            'UTILS: EventBus or CONSTANTS not available, cannot publish system error'
          );
        return false;
      }
    },

    /* Browser Detection */
    /**
     * Detects iOS Safari (iPhone, iPad, iPod)
     * Uses global window.isIOS set in theme.liquid for consistency
     * @returns {boolean} True if iOS Safari detected
     */
    isIOSSafari() {
      // Uses global window.isIOS defined in layout/theme.liquid
      return window.isIOS;
    },

    /**
     * iOS-safe scrollIntoView with smooth behavior fallback
     * iOS Safari has issues with smooth scroll behavior:
     * - Only supported in iOS 15.4+ (March 2022)
     * - Respects "Reduce Motion" accessibility setting
     * - Can have timing race conditions
     *
     * This helper uses instant scroll on iOS for reliability
     *
     * @param {Element} element - The element to scroll to
     * @param {Object} options - scrollIntoView options
     * @returns {void}
     */
    scrollIntoViewSafe(element, options = {}) {
      if (!element) return;

      const isIOS = this.isIOSSafari();

      // 🚨 `behavior: 'smooth'` is NOT auto-downgraded by the browser under
      // prefers-reduced-motion -- that coercion only happens for the CSS
      // `scroll-behavior` property, never for the scrollIntoView option. So a
      // customer who asked their OS for reduced motion still got a full smooth
      // scroll from every caller of this helper. Honouring it HERE rather than
      // at each call site is the whole reason this wrapper exists (it already
      // owns the iOS coercion for the same shape of problem), and it is why the
      // doc comment above has always cited Reduce Motion.
      //
      // Wrapped: matchMedia is absent in some test/jsdom harnesses, and a
      // throw here would take down a scroll rather than a preference.
      let prefersReducedMotion = false;
      try {
        prefersReducedMotion =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (_) {
        prefersReducedMotion = false;
      }

      // On iOS, use instant scroll for reliability (no smooth behavior)
      // On other browsers, preserve the requested behavior (usually 'smooth')
      const scrollOptions =
        isIOS || prefersReducedMotion
          ? { ...options, behavior: 'auto' } // 'auto' = instant scroll
          : options;

      element.scrollIntoView(scrollOptions);
    },

    /**
     * Resolves once the configurator has gone QUIET: no selection, state or
     * cascade activity for `quietMs`, capped at `maxMs`. The ONE shared settle
     * signal for "Build this" (md-config-support-build-preset.js: diff after
     * an apply, undo, share-load) and the render hook
     * (imageExportManager.renderConfigurationImage).
     *
     * Why a quiet window and not a COMPLETED event: the context-less
     * scheduleTermination COMPLETED is gated on
     * `cascadeDepth > 0 || autoAppliedCategories.size > 0` and may never
     * fire at all. The context-tagged COMPLETED fires BEFORE the queued
     * follow-on cascades and the late nested-tier/composite restores
     * (footguns "?config= Recovery Must Resume Nested Tiers" and
     * "reconcileRestoredGateAnswers() Runs Before Composite-Category Cascade
     * Restoration Finishes") have run, so diffing there under-reports.
     *
     * Only needs the bus at CALL time, so a lazy-loaded caller works.
     * Never rejects.
     *
     * @param {Object} [options]
     * @param {number} [options.quietMs] - default 2 x the coordinator's
     *   TERMINATION_DEBOUNCE_MS (50 -> 100ms)
     * @param {number} [options.maxMs=2000] - hard cap
     * @returns {Promise<{idle: boolean, waitedMs: number, activityCount: number}>}
     *   idle:false means the cap was hit while activity was still arriving.
     */
    waitForConfiguratorIdle(options) {
      const opts = options || {};
      const ns = window.MurphyDoorConfigurator || {};
      const bus = ns.CoreEventBus;
      const EVENTS = (ns.CONSTANTS && ns.CONSTANTS.EVENTS) || {};
      const debounce =
        (ns.cascadeAutoSelectionCoordinator &&
          ns.cascadeAutoSelectionCoordinator.CASCADE_CONFIG &&
          ns.cascadeAutoSelectionCoordinator.CASCADE_CONFIG
            .TERMINATION_DEBOUNCE_MS) ||
        50;
      const quietMs =
        typeof opts.quietMs === 'number' ? opts.quietMs : debounce * 2;
      const maxMs = typeof opts.maxMs === 'number' ? opts.maxMs : 2000;
      const startedAt = Date.now();

      return new Promise(function (resolve) {
        const unsubs = [];
        let quietTimer = null;
        let capTimer = null;
        let activityCount = 0;
        let done = false;

        const finish = function (idle) {
          if (done) return;
          done = true;
          clearTimeout(quietTimer);
          clearTimeout(capTimer);
          unsubs.forEach(function (unsubscribe) {
            try {
              unsubscribe();
            } catch (error) {
              /* already gone */
            }
          });
          resolve({
            idle: idle,
            waitedMs: Date.now() - startedAt,
            activityCount: activityCount,
          });
        };
        const arm = function (windowMs) {
          clearTimeout(quietTimer);
          quietTimer = setTimeout(
            function () {
              finish(true);
            },
            typeof windowMs === 'number' ? windowMs : quietMs
          );
        };
        const onActivity = function () {
          activityCount++;
          arm();
        };
        // apo_style: a material change schedules the material remap
        // (md-config-bl-material-remap-coordinator.js) SETTLE_MS (200ms) after
        // the LAST MATERIAL_PREFERENCE_APPLIED -- longer than the default quiet
        // window, so without this the settle resolved before the remap applied
        // and its re-render landed after the caller had diffed and re-rung
        // the tiles. Hold the window open until the remap has had its turn
        // (its applies then re-arm through SELECTION_VARIANT_APPLIED).
        const onMaterialPreference = function () {
          activityCount++;
          let remapSettleMs = 0;
          try {
            const remap = ns.materialRemapCoordinator;
            const experience =
              ns.conductor &&
              typeof ns.conductor._getConfiguratorExperience === 'function'
                ? ns.conductor._getConfiguratorExperience()
                : null;
            if (
              experience === 'apo_style' &&
              remap &&
              typeof remap.SETTLE_MS === 'number'
            ) {
              remapSettleMs = remap.SETTLE_MS;
            }
          } catch (error) {
            remapSettleMs = 0;
          }
          arm(quietMs + remapSettleMs);
        };

        if (bus && typeof bus.subscribe === 'function') {
          [
            EVENTS.SELECTION_VARIANT_APPLIED,
            EVENTS.SELECTION_AUTO_APPLY_REQUEST,
            EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
            EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
            EVENTS.STATE_CHANGED,
            EVENTS.STATE_CHANGE_COORDINATED,
            EVENTS.STATE_SELECTION_CLEARED,
            EVENTS.STATE_SELECTION_REMOVED,
            EVENTS.STATE_CATEGORY_COMPLETED,
            EVENTS.STATE_CATEGORY_INCOMPLETE,
            EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
            EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
            EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
            EVENTS.FORBIDDEN_AUTO_APPLY_TRIGGERED,
          ]
            .filter(function (type) {
              return typeof type === 'string';
            })
            .forEach(function (type) {
              try {
                const unsubscribe = bus.subscribe(type, onActivity);
                if (typeof unsubscribe === 'function') unsubs.push(unsubscribe);
              } catch (error) {
                /* a bus that rejects one type still observes the rest */
              }
            });
          if (typeof EVENTS.MATERIAL_PREFERENCE_APPLIED === 'string') {
            try {
              const unsubscribe = bus.subscribe(
                EVENTS.MATERIAL_PREFERENCE_APPLIED,
                onMaterialPreference
              );
              if (typeof unsubscribe === 'function') unsubs.push(unsubscribe);
            } catch (error) {
              /* the default activity set above still applies */
            }
          }
        }

        capTimer = setTimeout(function () {
          finish(false);
        }, maxMs);
        arm();
      });
    },

    /**
     * Write the browser URL WITHOUT waking third-party history hooks.
     *
     * MageWorx APO patches the history INSTANCE — `history.replaceState` is an
     * own property of the instance, while `History.prototype.replaceState`
     * stays `[native code]`. APO's wrapper treats every write as a product
     * navigation and responds by re-fetching the entire PDP and re-parsing it
     * with DOMParser into a fresh ~11,000-node Document that it then retains.
     *
     * The configurator writes the URL once per option selection, so that is
     * ~15,000 retained DOM nodes per interaction. Measured on production:
     * 35.7 MB of HTML parsed across 15 clicks, 226,065 nodes still retained
     * after a forced GC. That is the mechanism behind the "using the
     * configurator heats the device" QA finding — NOT a listener leak in our
     * own code (net listener growth is ~50/click and removeEventListener calls
     * outnumber addEventListener calls).
     *
     * Calling through the prototype writes a byte-identical URL while skipping
     * every instance wrapper. Microsoft Clarity and gtag also wrap the instance
     * to count SPA navigations; a `?config=` state write is not a navigation,
     * so bypassing them drops spurious same-page pageviews rather than real
     * tracking — configurator interactions are reported via `md_element_click`.
     *
     * Falls back to the instance method when the prototype is missing or has
     * itself been patched, so a platform change degrades to the previous
     * behaviour instead of throwing and losing URL state altogether.
     *
     * @param {Object} state - History state object
     * @param {string} title - History title (ignored by browsers)
     * @param {string|URL} url - The URL to write
     * @returns {boolean} True when the write used the pristine prototype
     */
    replaceStateQuietly(state, title, url) {
      const nativeReplaceState =
        typeof History === 'function' ? History.prototype.replaceState : null;

      if (typeof nativeReplaceState === 'function') {
        // 🚨 `typeof === 'function'` DOES NOT MEAN PRISTINE. A patched
        // prototype is also a function, and the previous version called it and
        // returned `true` regardless -- asserting "APO never saw this write" in
        // exactly the case where APO would have seen it. A return value that
        // lies in its failure case is worse than no return value at all.
        //
        // Hot path (once per option selection): ONE identity comparison, no
        // allocation, no toString. Do not replace this with a per-call
        // Function.prototype.toString check.
        const pristine = nativeReplaceState === _rsqOriginalProto;
        if (!pristine) {
          _rsqReportRegression(
            'History.prototype.replaceState was replaced after page load'
          );
        }

        nativeReplaceState.call(window.history, state, title, url);
        return pristine;
      }

      // No prototype method at all -- fall back to the instance, which is the
      // one MageWorx APO wraps. This silently restores the full PDP re-parse
      // (~11,000 retained nodes per option selection), so it must not pass
      // unremarked even though it should be unreachable.
      _rsqReportRegression('History.prototype.replaceState unavailable');
      window.history.replaceState(state, title, url);
      return false;
    },
  });
  _rsqAuditOriginalPrototype();
})(window);
